<script lang="ts">
/**
 * One numeric field the pad can edit. Structurally compatible with
 * `TypedInputField` (src/drawing/draggers/types.ts) on purpose, so a dragger's
 * typed-input spec can be handed straight to `:fields` — the drawing layer
 * declares its own copy rather than importing from a component.
 */
export interface KeypadField {
  key: string;
  /** Short text shown on the tab. Ignored when `icon` is set. */
  label?: string;
  /** Phosphor icon name shown on the tab instead of `label`. */
  icon?: string;
  /** Whether the sign button applies to this field. Defaults to true. */
  allowNegative?: boolean;
}

/**
 * One selectable set of fields. Structurally compatible with `TypedInputMode`
 * (src/drawing/draggers/types.ts), same reasoning as `KeypadField` above.
 */
export interface KeypadMode {
  key: string;
  /** Label for the selector button, already translated. */
  label: string;
  fields: KeypadField[];
}
</script>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { Z_INDEX_MODAL } from "@/components/canvas/layouts/constants";
import {
  bottomLeftClearance,
  toolbarWidth,
} from "@/stores/CanvasLayoutState";
import MeasurementKeypadState from "@/stores/MeasurementKeypadState";
import { isCanvasDrawing } from "@/stores/CanvasViewState";
import { isTypingTarget } from "@/utils/keyboardGuards";
import { getCanvasIconScale } from "@/composable/useCanvasUiScale";

/**
 * Floating numeric keypad — the single input surface for both PC and touch.
 * Custom-built (no Element Plus): a plain teleported, fixed-position panel, its
 * own drag-to-reposition, and its own open/close/focus handling. Opened/closed
 * via a keyboard toggle button (MeasurementKeypadState.isOpen), not by tapping
 * a field. Lets the user switch which field is being edited and validate
 * (commit) without leaving the keypad.
 *
 * Deliberately dumb: it knows nothing about what the numbers mean. The caller
 * supplies the title and the modes, so the same pad serves every tool — the
 * draw tools and MOVE/COPY (X/Y or L/∠ relative to an origin), ROTATE (an
 * angle) and SCALE (a factor, then a length) — see TypedInputPanel.vue, its
 * single controller.
 *
 * A mode is one selectable pair of fields. The selector only appears when the
 * step offers more than one, so ROTATE and SCALE never see it.
 *
 * Non-modal by construction (no full-screen overlay is ever rendered), so
 * the canvas stays pannable/zoomable while it's open. It opens bottom-left, next
 * to the left toolbar; the header can be dragged to reposition it from there —
 * the offset is session-only (MeasurementKeypadState),
 * not persisted, and shared by both callers so the pad stays where the user
 * put it.
 *
 * The panel carries a `.keypad-panel` class specifically so the shared
 * `isShortcutBlockingTarget` guard (used by KeyHandler/SequentialDragger/
 * TwoPointDragger/BasePointDragger) recognizes it and skips Ctrl+Z/Delete
 * while it has focus. Escape deliberately goes through to the active tool, and
 * the pad never closes on it — see src/utils/keyboardGuards.ts. Element Plus's ElDialog used to
 * provide that for free via its own `.el-dialog` class + focus trap; now
 * that we don't use it, we manage focus ourselves (see watch(isVisible)).
 * This component's own key handler intentionally uses the narrower
 * `isTypingTarget` instead — it IS the modal, so it must keep listening
 * while it has focus, not skip because of it.
 */

const props = defineProps<{
  /** Panel header, already translated. */
  title: string;
  /** Selectable field sets. A single entry hides the mode selector. */
  modes: KeypadMode[];
  activeModeKey: string;
  values: Record<string, string>;
  activeKey: string;
  /** Optional read-only figure shown above the fields, already translated. */
  hintLabel?: string;
  hintValue?: string;
}>();

const emit = defineEmits<{
  "update:activeKey": [value: string];
  "update:activeModeKey": [value: string];
  "field-input": [key: string, value: string];
  validate: [];
}>();

/** Fields of the active mode — everything below works off these, not all modes. */
const fields = computed<KeypadField[]>(
  () => props.modes.find((mode) => mode.key === props.activeModeKey)?.fields ?? []
);

function selectMode(key: string) {
  if (key === props.activeModeKey) return;
  emit("update:activeModeKey", key);
}

const isVisible = computed({
  get: () => MeasurementKeypadState.isOpen,
  set: (value: boolean) => {
    MeasurementKeypadState.isOpen = value;
  },
});

/** Gap between the pad and the left toolbar / the bottom edge of the screen. */
const KEYPAD_EDGE_GAP = 12;

// Anchored bottom-left, just right of the floating left toolbar (ortho /
// polar stay reachable while typing a distance), so it stays clear of the
// drawing's center and of the bottom-center selection bar — above the
// horizontal ruler or the collapsed text dock. The dragged offset is applied
// on top of that anchor.
const panelStyle = computed(() => ({
  zIndex: Z_INDEX_MODAL,
  left: `${toolbarWidth.value + KEYPAD_EDGE_GAP}px`,
  bottom: `${bottomLeftClearance.value + KEYPAD_EDGE_GAP}px`,
  transform: `translate(${MeasurementKeypadState.position.x}px, ${MeasurementKeypadState.position.y}px)`,
}));

const activeValue = computed(() => props.values[props.activeKey] ?? "");

const activeField = computed(() =>
  fields.value.find((field) => field.key === props.activeKey)
);

/** Fields opt out of the sign button (lengths, factors); default is on. */
const allowsNegative = computed(() => activeField.value?.allowNegative !== false);

/**
 * Whether the active field's content is "selected", like a native input after
 * a select-all: the next digit or decimal point replaces the value instead of
 * extending it, and backspace clears it. Set whenever a field is picked (tap
 * on its tab, Tab / Shift+Tab, the controller moving to a new step's first
 * field), so entering a new value never starts with clearing the old one.
 */
const isActiveValueSelected = ref(true);

watch(
  () => props.activeKey,
  () => {
    isActiveValueSelected.value = true;
  }
);

function selectField(key: string) {
  // Re-tapping the active field re-selects it too (the watch doesn't fire).
  isActiveValueSelected.value = true;
  emit("update:activeKey", key);
}

/** The active value to build on: empty while it is selected. */
function editableValue(): string {
  return isActiveValueSelected.value ? "" : activeValue.value;
}

function pressDigit(digit: string) {
  emit("field-input", props.activeKey, editableValue() + digit);
  isActiveValueSelected.value = false;
}

function pressDecimal() {
  const base = editableValue();
  if (base.includes(".")) return;
  emit("field-input", props.activeKey, (base === "" ? "0" : base) + ".");
  isActiveValueSelected.value = false;
}

function pressSign() {
  if (!allowsNegative.value) return;
  // On a selected value, starts a fresh negative entry, as "-" over a
  // select-all would in a native input; otherwise flips the sign.
  if (isActiveValueSelected.value) {
    emit("field-input", props.activeKey, "-");
    isActiveValueSelected.value = false;
    return;
  }
  const current = activeValue.value;
  const next = current.startsWith("-") ? current.slice(1) : `-${current}`;
  emit("field-input", props.activeKey, next);
}

function pressBackspace() {
  emit("field-input", props.activeKey, editableValue().slice(0, -1));
  isActiveValueSelected.value = false;
}

function resetActiveField() {
  emit("field-input", props.activeKey, "0");
  isActiveValueSelected.value = true;
}

function validate() {
  emit("validate");
  // The fields go back to mirroring the live state: the next value typed
  // replaces it.
  isActiveValueSelected.value = true;
}

function cycleField(direction: 1 | -1) {
  const keys = fields.value.map((field) => field.key);
  if (keys.length === 0) return;
  const index = keys.indexOf(props.activeKey);
  const next = (index + direction + keys.length) % keys.length;
  emit("update:activeKey", keys[next]);
}

function close() {
  isVisible.value = false;
}

// ── Focus management ────────────────────────────────────────────────────
// Without Element Plus's built-in focus trap, we focus the panel ourselves
// on open so isShortcutBlockingTarget's ".keypad-panel" check engages
// immediately, even if the user hasn't clicked inside it yet (e.g. they
// just opened it from the toolbar toggle button, which lives outside the
// panel).
const panelRef = ref<HTMLElement | null>(null);

watch(isVisible, (open) => {
  if (open) {
    isActiveValueSelected.value = true;
    nextTick(() => panelRef.value?.focus());
  }
});

// ── Physical keyboard support ───────────────────────────────────────────
// Lets PC type digits/backspace/enter directly instead of clicking buttons —
// this is what replaced the old docked native-input row: same keys, same
// field-cycling (Tab), routed through the same pressDigit/pressBackspace/etc.
// as a button tap so both input paths stay in sync.
function handleKeydown(event: KeyboardEvent) {
  // Narrower than isShortcutBlockingTarget on purpose: this keypad IS the
  // modal, so it must keep handling keys while it has focus (inside
  // .keypad-panel), not skip because of it. Only bail if some unrelated
  // real text input elsewhere has focus.
  if (!isVisible.value || isTypingTarget(event.target)) return;

  if (event.key >= "0" && event.key <= "9") {
    event.preventDefault();
    pressDigit(event.key);
  } else if (event.key === "." || event.key === ",") {
    event.preventDefault();
    pressDecimal();
  } else if (event.key === "-") {
    event.preventDefault();
    pressSign();
  } else if (event.key === "Backspace") {
    event.preventDefault();
    pressBackspace();
  } else if (event.key === "Delete") {
    event.preventDefault();
    resetActiveField();
  } else if (event.key === "Enter") {
    event.preventDefault();
    validate();
  } else if (event.key === "Tab") {
    event.preventDefault();
    cycleField(event.shiftKey ? -1 : 1);
  }
  // No Escape branch on purpose: it is left to the active tool (finish the
  // polyline, drop point A, cancel the command) — see keyboardGuards.ts.
}

onMounted(() => {
  window.addEventListener("keydown", handleKeydown);
});
onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleKeydown);
});

// ── Drag-to-reposition (pointer events cover mouse/touch/pen) ──────────────
interface DragStart {
  pointerX: number;
  pointerY: number;
  originX: number;
  originY: number;
  minDx: number;
  maxDx: number;
  minDy: number;
  maxDy: number;
}

let dragStart: DragStart | null = null;

function clamp(value: number, a: number, b: number): number {
  const min = Math.min(a, b);
  const max = Math.max(a, b);
  return Math.min(Math.max(value, min), max);
}

function onHeaderPointerDown(event: PointerEvent) {
  const rect = panelRef.value?.getBoundingClientRect();
  if (!rect) return;
  // Le panneau est zoomé (classe canvas-icon-scaled) : son translate() s'exprime
  // en pixels zoomés alors que le pointeur et le rectangle sont en pixels
  // écran — tout est ramené dans l'espace du panneau.
  const scale = getCanvasIconScale();
  dragStart = {
    pointerX: event.clientX,
    pointerY: event.clientY,
    originX: MeasurementKeypadState.position.x,
    originY: MeasurementKeypadState.position.y,
    minDx: -rect.left / scale,
    maxDx: (window.innerWidth - rect.width - rect.left) / scale,
    minDy: -rect.top / scale,
    maxDy: (window.innerHeight - rect.height - rect.top) / scale,
  };
  window.addEventListener("pointermove", onHeaderPointerMove);
  window.addEventListener("pointerup", onHeaderPointerUp);
}

function onHeaderPointerMove(event: PointerEvent) {
  if (!dragStart) return;
  const scale = getCanvasIconScale();
  const dx = clamp(
    (event.clientX - dragStart.pointerX) / scale,
    dragStart.minDx,
    dragStart.maxDx,
  );
  const dy = clamp(
    (event.clientY - dragStart.pointerY) / scale,
    dragStart.minDy,
    dragStart.maxDy,
  );
  MeasurementKeypadState.position.x = dragStart.originX + dx;
  MeasurementKeypadState.position.y = dragStart.originY + dy;
}

function onHeaderPointerUp() {
  dragStart = null;
  window.removeEventListener("pointermove", onHeaderPointerMove);
  window.removeEventListener("pointerup", onHeaderPointerUp);
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isVisible"
      ref="panelRef"
      class="keypad-panel canvas-icon-scaled"
      :class="{ 'drawing-active': isCanvasDrawing }"
      tabindex="-1"
      :style="panelStyle"
    >
      <div class="keypad-header" @pointerdown="onHeaderPointerDown">
        <PhosphorIcon name="dots-six-vertical" :size="14" class="keypad-grip" />
        <span class="keypad-title">{{ title }}</span>
        <button type="button" class="keypad-close" @pointerdown.stop @click="close">
          <PhosphorIcon name="x" :size="14" />
        </button>
      </div>

      <div class="keypad-content">
        <div v-if="hintLabel" class="keypad-hint">
          <span>{{ hintLabel }}</span>
          <span class="keypad-hint-value">{{ hintValue }}</span>
        </div>

        <div v-if="modes.length > 1" class="mode-switch">
          <button
            v-for="mode in modes"
            :key="mode.key"
            type="button"
            class="mode-btn"
            :class="{ active: mode.key === activeModeKey }"
            @click="selectMode(mode.key)"
          >
            {{ mode.label }}
          </button>
        </div>

        <div class="field-tabs">
          <button
            v-for="field in fields"
            :key="field.key"
            type="button"
            class="field-tab"
            :class="{ active: field.key === activeKey }"
            @click="selectField(field.key)"
          >
            <span class="field-tab-label">
              <PhosphorIcon v-if="field.icon" :name="field.icon" :size="12" />
              <template v-else>{{ field.label }}</template>
            </span>
            <span
              class="field-tab-value"
              :class="{
                selected:
                  field.key === activeKey &&
                  isActiveValueSelected &&
                  !!values[field.key],
              }"
            >{{ values[field.key] || "—" }}</span>
          </button>
        </div>

        <div class="keypad-grid">
          <button type="button" class="keypad-btn" @click="pressDigit('7')">
            7
          </button>
          <button type="button" class="keypad-btn" @click="pressDigit('8')">
            8
          </button>
          <button type="button" class="keypad-btn" @click="pressDigit('9')">
            9
          </button>
          <button type="button" class="keypad-btn" @click="pressDigit('4')">
            4
          </button>
          <button type="button" class="keypad-btn" @click="pressDigit('5')">
            5
          </button>
          <button type="button" class="keypad-btn" @click="pressDigit('6')">
            6
          </button>
          <button type="button" class="keypad-btn" @click="pressDigit('1')">
            1
          </button>
          <button type="button" class="keypad-btn" @click="pressDigit('2')">
            2
          </button>
          <button type="button" class="keypad-btn" @click="pressDigit('3')">
            3
          </button>
          <button
            type="button"
            class="keypad-btn"
            :disabled="!allowsNegative"
            @click="pressSign()"
          >
            -
          </button>
          <button type="button" class="keypad-btn" @click="pressDigit('0')">
            0
          </button>
          <button type="button" class="keypad-btn" @click="pressDecimal()">
            .
          </button>
        </div>

        <div class="keypad-actions">
          <button type="button" class="keypad-btn" @click="pressBackspace()">
            <PhosphorIcon name="backspace" :size="14" />
          </button>
          <button type="button" class="keypad-btn" @click="resetActiveField()">Del</button>
          <button type="button" class="keypad-btn keypad-validate" @click="validate()">
            {{ $t("canvas:tools.options.keypad.validate") }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.keypad-panel {
  // left/bottom come from panelStyle (anchored next to the left toolbar).
  position: fixed;
  width: 200px;
  background-color: var(--canvas-bg);
  border: 1px solid var(--canvas-border);
  border-radius: 8px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6);
  outline: none;
}

.keypad-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 6px 6px 8px;
  border-bottom: 1px solid var(--canvas-border);
  cursor: move;
  touch-action: none;
  user-select: none;
}

.keypad-grip {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.4);
}

.keypad-title {
  flex: 1;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
}

.keypad-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: rgba(255, 255, 255, 0.5);

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: var(--canvas-bg-hover);
      color: rgba(255, 255, 255, 0.9);
    }
  }
}

.keypad-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px;
}

.keypad-hint {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
  padding: 1px 2px 3px;
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.45);
}

.keypad-hint-value {
  color: rgba(255, 255, 255, 0.7);
  font-variant-numeric: tabular-nums;
}

.mode-switch {
  display: flex;
  gap: 2px;
  padding: 2px;
  border-radius: 5px;
  background-color: var(--canvas-bg-hover);
}

.mode-btn {
  flex: 1;
  padding: 3px 0;
  border: none;
  border-radius: 3px;
  background: transparent;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.65rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(.active) {
      color: rgba(255, 255, 255, 0.85);
    }
  }

  &.active {
    background-color: var(--bs-primary);
    color: #fff;
  }
}

.field-tabs {
  display: flex;
  gap: 2px;
  border-bottom: 1px solid var(--canvas-border);
  margin-bottom: 2px;
}

.field-tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  padding: 3px 2px 5px;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  background: transparent;
  cursor: pointer;
  transition:
    color 0.15s,
    border-color 0.15s;
}

.field-tab-label {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 12px;
  font-size: 0.65rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
}

.field-tab-value {
  padding: 0 2px;
  border-radius: 2px;
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.75);

  // Mimics a native text selection: the next keystroke replaces this value.
  &.selected {
    background-color: var(--bs-primary);
    color: #fff;
  }
}

.field-tab.active {
  border-bottom-color: var(--bs-primary);

  .field-tab-label {
    color: #fff;
  }
}

.keypad-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3px;
}

.keypad-actions {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3px;
}

.keypad-btn {
  height: 30px;
  padding: 0;
  border: 1px solid var(--canvas-border);
  border-radius: 4px;
  background-color: var(--canvas-bg-hover);
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;

  // Survol à la souris seulement : au doigt ou au stylet, le navigateur garde
  // :hover sur la dernière touche appuyée, qui restait bleue.
  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      background-color: var(--canvas-btn-active-bg);
    }
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
}

.keypad-validate {
  color: #fff;
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
  font-weight: 600;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      filter: brightness(1.1);
    }
  }
}
</style>
